import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  attachClosestEdge,
  Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { ExpandedState, Row } from '@tanstack/react-table'
import styled from 'styled-components'

import { DraggableStyleProps, DraggableStyles } from 'src/components/DragIndicator'
import DropIndicator from 'src/components/DropIndicator'
import useDisclosure from 'src/hooks/use-disclosure'

import { useTableContext } from './context'
import TableCell from './TableCell'
import { TableRowDragState } from './types'
import { getStickyColumnProperties, isTableRowDragState } from './util'

const StyledTBody = styled.tbody<{ $isDragging: boolean } & DraggableStyleProps>`
  ${DraggableStyles}
`
const ExpandedRowTD = styled.td`
  padding: 0px;
  overflow: clip;
  isolation: isolate;
`
const ExpandedRowBoxShadowContainer = styled.div`
  position: relative;
  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    pointer-events: none;
    z-index: 1;
    box-shadow: 0px 0px 4px 0px ${({ theme }) => theme.colorBorder} inset;
  }
`

type Props<TData> = {
  expanded?: ExpandedState
  getRowExpandedContent?: (value: TData) => React.ReactNode
  headersByColumnKeyRef: RefObject<Map<string, HTMLTableCellElement>>
  isLastRow: boolean
  isRowSelectionEnabled: boolean
  isScrolledToLeft: boolean
  isScrolledToRight: boolean
  onHoveredRowChange?: (row: Row<TData> | undefined) => void
  onRowReordered?: (sourceRow: Row<TData>, destinationRow: Row<TData>, closestEdge: Edge) => void
  overrideReorderingWithinSameTableInvariant?: (rowDragState: TableRowDragState<TData>) => boolean
  row: Row<TData>
  tableContainerWidth: React.CSSProperties['width']
  verticalAlign: React.CSSProperties['verticalAlign']
  isRowReorderable: boolean
  isRowHighlighted: boolean
}
const TableRow = <TData,>({
  expanded,
  getRowExpandedContent,
  headersByColumnKeyRef,
  isLastRow,
  isRowHighlighted,
  isRowSelectionEnabled,
  isScrolledToLeft,
  isScrolledToRight,
  onHoveredRowChange,
  onRowReordered,
  overrideReorderingWithinSameTableInvariant,
  row,
  tableContainerWidth,
  verticalAlign,
  isRowReorderable,
}: Props<TData>) => {
  const { isOpen: isDragging, open: startDragging, close: stopDragging } = useDisclosure()
  const { uuid: tableUuid } = useTableContext()

  const invariant = useCallback(
    (dragState: TableRowDragState<TData>) => {
      if (overrideReorderingWithinSameTableInvariant) {
        return overrideReorderingWithinSameTableInvariant(dragState)
      }
      return dragState.tableUuid === tableUuid
    },
    [overrideReorderingWithinSameTableInvariant, tableUuid]
  )

  const draggableRef = useRef<HTMLTableSectionElement>(null)
  const dragHandleRef = useRef(null)
  const getDragData = useCallback(
    (): TableRowDragState<TData> => ({
      row,
      tableUuid,
    }),
    [row, tableUuid]
  )
  useEffect(() => {
    const element = draggableRef.current
    const dragHandle = dragHandleRef.current
    if (!element || !dragHandle || !onRowReordered || !isRowReorderable) {
      return
    }
    return draggable({
      dragHandle,
      element,
      getInitialData: getDragData,
      onDragStart: startDragging,
      onDrop: stopDragging,
    })
  }, [getDragData, isRowReorderable, onRowReordered, row, startDragging, stopDragging])

  const {
    isOpen: isDropTarget,
    open: prepareForDrop,
    close: stopPreparingForDrop,
  } = useDisclosure()
  const [closestEdge, setClosestEdge] = useState<Extract<Edge, 'top' | 'bottom'>>('top')
  useEffect(() => {
    const element = draggableRef.current
    if (!element || !onRowReordered) {
      return
    }
    return dropTargetForElements({
      canDrop: ({ source }) => {
        const dragData = source.data
        if (
          !isTableRowDragState<TData>(dragData) ||
          !invariant(dragData) ||
          dragData.row.id === row.id
        ) {
          return false
        }
        return true
      },
      element,
      getData: ({ input, element: targetElement }) => {
        const data = attachClosestEdge(getDragData(), {
          input,
          element: targetElement,
          allowedEdges: ['top', 'bottom'],
        })
        const newEdge = extractClosestEdge(data) as Extract<Edge, 'top' | 'bottom'> | undefined
        setClosestEdge(newEdge ?? 'top')
        return data
      },
      getDropEffect: () => 'move',
      onDragEnter: prepareForDrop,
      onDragLeave: stopPreparingForDrop,
      onDrop: stopPreparingForDrop,
    })
  }, [getDragData, invariant, onRowReordered, prepareForDrop, row, stopPreparingForDrop, tableUuid])

  useEffect(() => {
    return monitorForElements({
      onDrop: async ({ source, location }) => {
        const destination = location.current.dropTargets[0]
        if (!destination) {
          return
        }
        const sourceData = source.data
        const destinationData = destination.data
        if (
          !isTableRowDragState<TData>(sourceData) ||
          !isTableRowDragState<TData>(destinationData) ||
          !invariant(sourceData) ||
          !invariant(destinationData) ||
          destinationData.row.id !== row.id
        ) {
          return
        }

        onRowReordered?.(sourceData.row, destinationData.row, closestEdge)
      },
    })
  }, [closestEdge, invariant, onRowReordered, row.id])

  const handleMouseEnter = useCallback(() => onHoveredRowChange?.(row), [onHoveredRowChange, row])
  const handleMouseLeave = useCallback(() => onHoveredRowChange?.(undefined), [onHoveredRowChange])

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>, clickedRow: Row<TData>) => {
      if (e.ctrlKey || e.metaKey || !isRowSelectionEnabled) {
        return // don't toggle row if a link was ctrl+clicked, or if row selection is disabled
      }
      const handler = clickedRow.getToggleSelectedHandler()
      handler(e)
    },
    [isRowSelectionEnabled]
  )

  return (
    <StyledTBody
      $isDragging={isDragging}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={draggableRef}
    >
      {isDropTarget && (
        <tr style={{ display: 'block', maxHeight: 0, overflow: 'visible' }}>
          <td style={{ display: 'block', maxHeight: 0 }}>
            <DropIndicator
              edge={closestEdge}
              fixedLayout={{
                dropTargetBoundingClientRect: draggableRef.current?.getBoundingClientRect(),
                width: tableContainerWidth,
              }}
            />
          </td>
        </tr>
      )}
      <tr className="group/table-row">
        {row.getAllCells().map((cell, index, arr) => {
          const { isLastLeftStickyColumn, isFirstRightStickyColumn, left, right, stickiness } =
            getStickyColumnProperties(cell, index, arr, headersByColumnKeyRef)
          return (
            <TableCell
              cell={cell}
              dragHandleRef={dragHandleRef}
              isFirstRightStickyColumn={isFirstRightStickyColumn}
              isLastLeftStickyColumn={isLastLeftStickyColumn}
              isLastRow={isLastRow}
              isRowHighlighted={isRowHighlighted}
              isRowReorderable={isRowReorderable}
              isRowSelectionEnabled={isRowSelectionEnabled}
              isScrolledToLeft={isScrolledToLeft}
              isScrolledToRight={isScrolledToRight}
              key={cell.id}
              left={left}
              onClick={cell.column.columnDef.meta?.onClick ?? handleRowClick}
              right={right}
              stickiness={stickiness}
              verticalAlign={verticalAlign}
            />
          )
        })}
      </tr>
      {typeof expanded !== 'boolean' && !!row.getIsExpanded() && (
        <tr>
          <ExpandedRowTD colSpan={100}>
            <ExpandedRowBoxShadowContainer>
              {getRowExpandedContent?.(row.original) || null}
            </ExpandedRowBoxShadowContainer>
          </ExpandedRowTD>
        </tr>
      )}
    </StyledTBody>
  )
}

export default React.memo(TableRow) as typeof TableRow
